import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../contexts/ToastContext';
import {
  getAdminCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  getCategories,
  getTrainers,
  updateSystemSettings,
  getPublicSettingsBulk,
  generateCourseAI,
} from '../../api/client';
import type { Course } from '../../types/course';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Skeleton from '../../components/ui/Skeleton';
import CourseForm from './CourseForm';
import {
  Search,
  Plus,
  Edit3,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  Star,
  AlertTriangle,
  X,
  BookOpen,
  HelpCircle,
  IndianRupee,
  Home,
  Sparkles,
} from 'lucide-react';

interface Category {
  id: number;
  name: string;
}

interface Trainer {
  id: number;
  name: string;
}

const emptyForm = {
  title: '',
  slug: '',
  short_description: '',
  full_description: '',
  price: '',
  discount_price: '',
  status: 'draft',
  category_id: '',
  trainer_id: '',
  course_image: '',
  banner_image: '',
  intro_video: '',
  duration: '',
  benefits: [] as string[],
  skills_covered: [] as string[],
  seo_title: '',
  seo_description: '',
  meta_keywords: '',
  sort_order: '0',
};

export default function AdminCourses(): React.JSX.Element {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [trainers, setTrainers] = useState<Trainer[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Course | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [featuredCourseId, setFeaturedCourseId] = useState<string>('');
  const [settingHomepage, setSettingHomepage] = useState<number | null>(null);

  // AI generation
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await getAdminCourses(params);
      const list = Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];
      setCourses(list as Course[]);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load courses', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, toast]);

  const fetchMeta = useCallback(async () => {
    try {
      const [catRes, trainerRes] = await Promise.all([
        getCategories(),
        getTrainers(),
      ]);
      setCategories(Array.isArray(catRes.data) ? catRes.data : catRes.data?.data || []);
      setTrainers(Array.isArray(trainerRes.data) ? trainerRes.data : trainerRes.data?.data || []);
    } catch {
      // silent fail for meta
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    getPublicSettingsBulk(['featured_course_id'])
      .then((res) => {
        const id = res.settings?.featured_course_id ?? '';
        setFeaturedCourseId(String(id));
      })
      .catch(() => {});
  }, []);

  const setAsHomepage = async (course: Course) => {
    setSettingHomepage(course.id);
    try {
      await updateSystemSettings({
        settings: [{ key: 'featured_course_id', value: String(course.id), group_name: 'homepage', is_public: true }],
      });
      setFeaturedCourseId(String(course.id));
      toast.show(`"${course.title}" is now the homepage course`, 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to set homepage course', 'error');
    } finally {
      setSettingHomepage(null);
    }
  };

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const openCreate = () => {
    setEditingCourse(null);
    setForm({ ...emptyForm });
    setIsModalOpen(true);
  };

  const openEdit = (course: Course) => {
    setEditingCourse(course);
    setForm({
      title: course.title || '',
      slug: course.slug || '',
      short_description: course.short_description || '',
      full_description: course.full_description || '',
      price: String(course.price ?? ''),
      discount_price: String(course.discount_price ?? ''),
      status: course.status || 'draft',
      category_id: String(course.category_name ? findCategoryId(course.category_name) : ''),
      trainer_id: String(course.trainer_id ?? ''),
      course_image: course.course_image || '',
      banner_image: course.banner_image || '',
      intro_video: course.intro_video || '',
      duration: course.duration || '',
      benefits: Array.isArray(course.benefits) ? course.benefits : (course.benefits ? JSON.parse(course.benefits) : []),
      skills_covered: Array.isArray(course.skills_covered) ? course.skills_covered : (course.skills_covered ? JSON.parse(course.skills_covered) : []),
      seo_title: course.seo_title || '',
      seo_description: course.seo_description || '',
      meta_keywords: course.meta_keywords || '',
      sort_order: String(course.sort_order ?? '0'),
    });
    setIsModalOpen(true);
  };

  const findCategoryId = (name: string) => {
    const cat = categories.find((c) => c.name === name);
    return cat ? String(cat.id) : '';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCourse(null);
    setForm({ ...emptyForm });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: any = {
        ...form,
        price: Number(form.price) || 0,
        discount_price: Number(form.discount_price) || 0,
        category_id: form.category_id ? Number(form.category_id) : undefined,
        trainer_id: form.trainer_id ? Number(form.trainer_id) : undefined,
        sort_order: Number(form.sort_order) || 0,
        benefits: form.benefits,
        skills_covered: form.skills_covered,
      };
      if (editingCourse) {
        await updateCourse(editingCourse.id, payload);
        toast.show('Course updated successfully', 'success');
      } else {
        await createCourse(payload);
        toast.show('Course created successfully', 'success');
      }
      closeModal();
      fetchCourses();
    } catch (err: any) {
      toast.show(err.message || 'Failed to save course', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCourse(deleteTarget.id);
      toast.show('Course deleted successfully', 'success');
      setDeleteTarget(null);
      fetchCourses();
    } catch (err: any) {
      toast.show(err.message || 'Failed to delete course', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const toggleFeatured = async (course: Course) => {
    try {
      await updateCourse(course.id, {
        is_featured: course.is_featured ? 0 : 1,
      });
      setCourses((prev) =>
        prev.map((c) =>
          c.id === course.id ? { ...c, is_featured: c.is_featured ? 0 : 1 } : c
        )
      );
      toast.show('Featured status updated', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to update featured status', 'error');
    }
  };

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) {
      toast.show('Enter a topic first', 'error');
      return;
    }
    setAiLoading(true);
    try {
      const res = await generateCourseAI(aiTopic.trim());
      const data = res.data || res;
      setForm({
        title: data.title || '',
        slug: data.slug || '',
        short_description: data.short_description || '',
        full_description: data.full_description || '',
        price: String(data.price || ''),
        discount_price: String(data.discount_price || ''),
        status: 'draft',
        category_id: '',
        trainer_id: '',
        course_image: '',
        banner_image: '',
        intro_video: '',
        duration: data.duration || '',
        benefits: data.benefits || [],
        skills_covered: data.skills_covered || [],
        seo_title: data.seo_title || '',
        seo_description: data.seo_description || '',
        meta_keywords: data.meta_keywords || '',
        sort_order: '0',
      });
      setAiModalOpen(false);
      setAiTopic('');
      toast.show('AI content generated! Review and save.', 'success');
      setEditingCourse(null);
      setIsModalOpen(true);
    } catch (err: any) {
      toast.show(err.message || 'AI generation failed', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const toggleStatus = async (course: Course) => {
    const next = course.status === 'published' ? 'draft' : 'published';
    try {
      await updateCourse(course.id, { status: next });
      setCourses((prev) =>
        prev.map((c) => (c.id === course.id ? { ...c, status: next } : c))
      );
      toast.show(`Course ${next === 'published' ? 'published' : 'unpublished'}`, 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to update status', 'error');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Courses</h1>
            <p className="text-sm text-slate-500">Manage your courses and workshops</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAiModalOpen(true)} leftIcon={<Sparkles className="w-4 h-4" />}>
              Generate with AI
            </Button>
            <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
              Create Course
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search courses..."
              value={search}
              onChange={(e) => updateParam('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => updateParam('status', e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="pending_review">Pending Review</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Course</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Category</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Price</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Enrollments</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Revenue</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Status</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <Skeleton className="w-10 h-10 rounded-xl" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4"><Skeleton className="h-5 w-20" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-5 w-16" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-5 w-10" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-6 w-20 rounded-full" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-5 w-24" /></td>
                    </tr>
                  ))
                ) : (
                  courses.map((course) => (
                    <tr key={course.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {course.course_image ? (
                            <img
                              src={course.course_image}
                              alt={course.title}
                              className="w-10 h-10 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">
                              {course.title?.charAt(0)?.toUpperCase() || 'C'}
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{course.title}</p>
                            <p className="text-xs text-slate-500">{course.trainer_name || 'No trainer'}</p>
                          </div>
                          {course.is_featured ? (
                            <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold">FEATURED</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                          {course.category_name || 'Uncategorized'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm">
                          <span className="font-bold text-slate-900">₹{Number(course.discount_price).toLocaleString()}</span>
                          <span className="text-slate-400 line-through ml-2 text-xs">₹{Number(course.price).toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{course.enrollment_count}</td>
                      <td className="px-5 py-4 text-sm font-medium text-slate-900">
                        ₹{course.total_revenue ? Number(course.total_revenue).toLocaleString() : '0'}
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => toggleStatus(course)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
                            course.status === 'published'
                              ? 'bg-green-50 text-green-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {course.status === 'published' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {course.status}
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleFeatured(course)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              course.is_featured ? 'text-amber-500 bg-amber-50' : 'text-slate-400 hover:bg-slate-100'
                            }`}
                            title="Toggle Featured"
                          >
                            <Star className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setAsHomepage(course)}
                            disabled={settingHomepage === course.id}
                            className={`p-1.5 rounded-lg transition-colors ${
                              featuredCourseId === String(course.id)
                                ? 'text-green-600 bg-green-50'
                                : 'text-slate-400 hover:text-green-600 hover:bg-green-50'
                            }`}
                            title={featuredCourseId === String(course.id) ? 'Current Homepage' : 'Set as Homepage'}
                          >
                            <Home className="w-4 h-4" />
                          </button>
                          <Link
                            to={`/courses/${course.slug}`}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/admin/courses/${course.id}/modules`}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                            title="Curriculum"
                          >
                            <BookOpen className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/admin/courses/${course.id}/faq`}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors"
                            title="FAQ"
                          >
                            <HelpCircle className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => openEdit(course)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(course)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && courses.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">No courses found</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Course Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-900">
                  {editingCourse ? 'Edit Course' : 'Create Course'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <CourseForm
                form={form}
                setForm={setForm}
                onSubmit={handleSubmit}
                onCancel={closeModal}
                saving={saving}
                categories={categories}
                trainers={trainers}
                isEdit={!!editingCourse}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Delete Course?</h3>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                Are you sure you want to delete <span className="font-semibold text-slate-700">{deleteTarget.title}</span>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setDeleteTarget(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  isLoading={deleting}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* AI Generate Modal */}
      <AnimatePresence>
        {aiModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
            onClick={() => setAiModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Generate Course with AI
                </h2>
                <button onClick={() => setAiModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-slate-500">
                  Enter a topic and our AI will generate a complete course outline, description, benefits, and curriculum.
                </p>
                <Input
                  label="Topic"
                  placeholder="e.g. AI for Fraud Detection in Banking"
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAIGenerate()}
                />
              </div>
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setAiModalOpen(false)}>Cancel</Button>
                <Button onClick={handleAIGenerate} isLoading={aiLoading} leftIcon={<Sparkles className="w-4 h-4" />}>
                  Generate
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
}
